import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DocumentReferenceObjectView, DocumentReferencesView } from 'api';
import DocumentPreviewDrawerTab from 'components/Drawer/document-preview-drawer-tab/DocumentPreviewDrawerTab';
import NavigationConfig, {
  DrawerSecondaryAction,
} from 'components/Drawer/tab-drawer/models/navigation-config.interface';
import FilesService from 'services/documents';
import { useDocuments, useGetDocumentsById, useResponse, useSessionStorage } from 'services/hooks';
import useGetFileIconByExtension from 'utilities/getFileIconByExtension/getFileIconByExtension';

const filesService = new FilesService();

export interface UseDocumentTabsOptions {
  storageKey: string;
  companyId: string;
  measurementDateId?: number;
  documentTabCustomActions?: DrawerSecondaryAction[];
  documentSourceProps?: {
    documentMetadata?: Map<number, number>;
  };
}

/**
 * Manages document preview tab lifecycle: sessionStorage persistence (per browser tab),
 * data fetching via React Query, and NavigationConfig generation.
 */
export default function useDocumentTabs({
  storageKey,
  companyId,
  measurementDateId,
  documentTabCustomActions,
  documentSourceProps,
}: UseDocumentTabsOptions) {
  const [documentNavItems, setDocumentNavItems] = useSessionStorage<Record<string, string>>(
    storageKey,
    {},
    { migrateFromLocalOnce: true }
  );
  const { downloadDocument } = useDocuments();
  const downloadDocumentRef = useRef(downloadDocument);
  downloadDocumentRef.current = downloadDocument;
  const { errorNotification } = useResponse();
  const getFileIcon = useGetFileIconByExtension;
  const explicitlyClosedTabs = useRef<Set<string>>(new Set());

  const closeDocumentTab = useCallback(
    (tabId: string) => {
      explicitlyClosedTabs.current.add(tabId);
      setDocumentNavItems(prev => {
        const { [tabId]: removedTab, ...rest } = prev ?? {};
        return removedTab ? rest : rest;
      });
    },
    [setDocumentNavItems]
  );

  const openDocumentTab = useCallback(
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView): string => {
      const docId = document.document_reference_id.toString();

      if (documentNavItems?.[docId]) {
        return docId;
      }

      explicitlyClosedTabs.current.delete(docId);
      setDocumentNavItems(prev => ({
        ...prev,
        [docId]: reference?.id?.toString() ?? docId,
      }));

      return docId;
    },
    [documentNavItems, setDocumentNavItems]
  );

  const openDocumentById = useCallback(
    (docId: string): string => {
      if (documentNavItems?.[docId]) {
        return docId;
      }
      explicitlyClosedTabs.current.delete(docId);
      setDocumentNavItems(prev => ({ ...prev, [docId]: docId }));
      return docId;
    },
    [documentNavItems, setDocumentNavItems]
  );

  const openDocumentIds = useMemo(() => Object.keys(documentNavItems ?? {}), [documentNavItems]);

  const restoredIdsRef = useRef<Set<string> | null>(null);
  const restoredKeyRef = useRef<string | null>(null);
  const prunedStorageKeyRef = useRef<string | null>(null);
  if (restoredKeyRef.current !== storageKey) {
    restoredKeyRef.current = storageKey;
    restoredIdsRef.current = new Set(Object.keys(documentNavItems ?? {}));
    prunedStorageKeyRef.current = null;
  }

  // One-time restore check against a fresh MD documents list. Live opens stay.
  useEffect(() => {
    let cancelled = false;
    if (measurementDateId != null && prunedStorageKeyRef.current !== storageKey) {
      filesService
        .getReferencesByMeasurementDateId(String(measurementDateId))
        .then((docs: unknown) => {
          if (cancelled || prunedStorageKeyRef.current === storageKey) {
            return;
          }
          const allow = new Set(
            (Array.isArray(docs) ? docs : []).map((doc: DocumentReferencesView) => doc.document_reference_id.toString())
          );
          const restoredIds = restoredIdsRef.current ?? new Set<string>();
          prunedStorageKeyRef.current = storageKey;
          setDocumentNavItems(prev => {
            const entries = Object.entries(prev ?? {});
            const kept = entries.filter(([id]) => !restoredIds.has(id) || allow.has(id));
            if (kept.length === entries.length) {
              return prev ?? {};
            }
            return Object.fromEntries(kept);
          });
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [measurementDateId, setDocumentNavItems, storageKey]);

  const handleDocumentLoadError = useCallback(
    (failedDocumentId: string) => {
      closeDocumentTab(failedDocumentId);
      errorNotification(`Error loading document for preview: ${failedDocumentId}`);
    },
    [closeDocumentTab, errorNotification]
  );

  const { data: documentsData, isLoading } = useGetDocumentsById({
    documentIds: openDocumentIds,
    onDocumentLoadError: handleDocumentLoadError,
  });

  const documentTabConfigs = useMemo((): NavigationConfig[] => {
    if (!documentsData) {
      return [];
    }

    return Object.values(documentsData)
      .filter(doc => !explicitlyClosedTabs.current.has(doc.document_reference_id.toString()))
      .map(doc => {
        const docId = doc.document_reference_id.toString();
        const fileIcon = getFileIcon(doc.file.file_type);
        const citationNumber = documentSourceProps?.documentMetadata?.get(doc.document_reference_id);
        const tabTitle = citationNumber ? `[${citationNumber}] - ${doc.file.filename}` : doc.file.filename;

        const secondaryActions: DrawerSecondaryAction[] = [
          {
            title: 'Download original',
            onClick: async () => {
              await downloadDocumentRef.current(doc);
            },
          },
          ...(documentTabCustomActions?.map(action => ({
            ...action,
            onClick: async () => action.onClick(doc),
          })) ?? []),
        ];

        return {
          tabContent: <DocumentPreviewDrawerTab document={doc} companyId={companyId} />,
          tabTitle,
          uniqueId: docId,
          icon: fileIcon,
          secondaryActions,
        };
      });
  }, [documentsData, getFileIcon, documentSourceProps, companyId, documentTabCustomActions]);
  const documentFileTypeByTabId = useMemo(() => {
    if (!documentsData) {
      return {};
    }

    return Object.values(documentsData).reduce<Record<string, DocumentReferencesView['file']['file_type']>>(
      (acc, doc) => {
        acc[doc.document_reference_id.toString()] = doc.file.file_type;
        return acc;
      },
      {}
    );
  }, [documentsData]);

  return {
    documentTabConfigs,
    documentFileTypeByTabId,
    isLoading,
    openDocumentTab,
    openDocumentById,
    closeDocumentTab,
    openDocumentIds,
  };
}
